import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { RECORDS_PER_PAGE_OPTIONS } from '../../../../utils/constants';

import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import Button from '../../../common/button/Button';
import Table from '../../../common/table/Table';
import RoleModal from './role-modal/RoleModal';

import './user-role-management.scss';

const UserRoleManagement = ({
  staff,
  members,
  usernames,
  initialFilters,
  currentPage,
  totalPages,
  retrieveStaff,
  updateRoles,
}) => {
  const [recordsPerPage, setRecordsPerPage] = useState({ label: 20, value: 20 });
  const [member, setMember] = useState(undefined);
  const [username, setUsername] = useState(undefined);
  const [role, setRole] = useState(undefined);
  const [selectedStaffMember, setSelectedStaffMember] = useState(undefined);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const filters = useRef({});

  useEffect(() => {
    setRecordsPerPage(initialFilters.recordsPerPage);
    setMember(initialFilters.member);
    setUsername(initialFilters.username);
    if (initialFilters.admin) {
      setRole({ label: 'Admin', value: 'Admin' });
    } else if (initialFilters.facilitator) {
      setRole({ label: 'Facilitator', value: 'Facilitator' });
    } else {
      setRole(undefined);
    }

    filters.current = initialFilters;
  }, [initialFilters]);

  const handleRowClick = (staffMember) => {
    setSelectedStaffMember(staffMember);
    setShowRoleModal(true);
  };

  const haveFiltersChanged = () => {
    let definedKeys = 0;
    for (let key in filters.current) {
      if (filters.current[key] !== undefined && filters.current[key] !== null && filters.current[key] !== '') {
        definedKeys++;
      }
    }

    if (filters.current.page !== 0 || filters.current.recordsPerPage.value !== 20 || definedKeys !== 2) {
      return true;
    }

    return false;
  };

  const applyFilters = (page) => {
    filters.current = {
      recordsPerPage,
      member,
      page,
      username,
      admin: role?.value === 'Admin' ? 'admin' : undefined,
      facilitator: role?.value === 'Facilitator' ? 'facilitator' : undefined,
    };

    retrieveStaff(filters.current);
  };

  const removeFilters = () => {
    const recordsPerPage = RECORDS_PER_PAGE_OPTIONS[1];

    filters.current = {
      page: 0,
      recordsPerPage,
    };

    setRecordsPerPage(recordsPerPage);
    setMember(undefined);
    setUsername(undefined);
    setRole(undefined);

    retrieveStaff(filters.current);
  };

  return (
    <>
      <section className="user-role-management" data-testid="user-role-management">
        <section className="user-role-management__filter">
          <section className="user-role-management__filter__filters">
            <p>Filter</p>

            <label htmlFor="records-per-page">Records per Page</label>
            <Dropdown
              name="records-per-page"
              value={recordsPerPage}
              options={RECORDS_PER_PAGE_OPTIONS}
              onChange={(option) => setRecordsPerPage(option)}
            />

            <label htmlFor="member">Member</label>
            <Dropdown name="member" value={member} options={members} onChange={(option) => setMember(option)} />

            <label htmlFor="username">Username</label>
            <Dropdown name="username" value={username} options={usernames} onChange={(option) => setUsername(option)} />

            <label htmlFor="role">Role</label>
            <Dropdown
              name="role"
              value={role}
              options={[
                { label: 'Admin', value: 'Admin' },
                { label: 'Facilitator', value: 'Facilitator' },
              ]}
              onChange={(option) => setRole(option)}
            />
          </section>

          <section className="user-role-management__filter__button-container">
            <Button onClick={() => applyFilters(0)}>Apply Filter</Button>
            {haveFiltersChanged() && <Button onClick={() => removeFilters()}>Remove Filter</Button>}
          </section>
        </section>

        <section className="user-role-management__table">
          <section className="user-role-management__table__header">
            <h1>ATP Staff</h1>
          </section>

          <section className="user-role-management__table__container">
            <Table
              data={staff}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={applyFilters}
              onRowClick={handleRowClick}
              emptyMessage="No staff found"
              hiddenColumns={['personExternalReference']}
            />
          </section>
        </section>
      </section>

      <RoleModal
        show={showRoleModal}
        setShow={setShowRoleModal}
        staffMember={selectedStaffMember}
        setStaffMember={setSelectedStaffMember}
        updateRoles={updateRoles}
      />
    </>
  );
};

UserRoleManagement.propTypes = {
  staff: PropTypes.arrayOf(PropTypes.shape({})),
  members: PropTypes.arrayOf(PropTypes.shape({})),
  usernames: PropTypes.arrayOf(PropTypes.shape({})),
  initialFilters: PropTypes.shape({}).isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  retrieveStaff: PropTypes.func.isRequired,
  updateRoles: PropTypes.func.isRequired,
};

UserRoleManagement.defaultProps = {
  staff: [],
  members: [],
  usernames: [],
};

export default UserRoleManagement;
